import { useMutation } from '@apollo/client';
import { Formik } from 'formik';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { UPDATE_DRAFT_VARIABLES } from '../../../graphql/graphQLvariables/updateDraft.variables';
import { UPDATE_DRAFT_MUTATION } from '../../../graphql/resolvers/draftMutations/updateDraft.gql';
import { useDrafts } from '../../../hooks/useDrafts';
import {
  DocumentStatus,
  DraftInterfaceState,
  PostDraftInterface,
} from '../../../interfaces/post.interface';
import type {
  EditPostPayload,
  TextBlock,
} from '../../../interfaces/textEditor.interface';
import { UserState } from '../../../interfaces/user.interface';
import { draftSlice } from '../../../store/draft.store';
import { userSlice } from '../../../store/user.store';
import { handleBlockTypeChange } from '../../TextEditor/handlers/handleBlockTypeChange';
import { DraftFormRender } from '../DraftFormRender';

interface EditDraftFormProps {
  handleClose: () => void;
  isEditing?: boolean;
}

/**
 * EditDraftForm is a React functional component for editing a post.
 *
 * This component leverages Formik for form state management and handles various
 * actions such as changing the type of content blocks, managing menu anchors,
 * and saving the edited post.
 *
 * @param {EditPostFormProps} props - The properties passed to the component.
 * @param {Object} props.post - The post object to be edited.
 *
 * @component
 */
export const EditDraftForm: React.FC<EditDraftFormProps> = (
  props: EditDraftFormProps,
) => {
  const params = useParams();
  const { draftId, username } = params;
  const dispatch = useDispatch();
  const {
    GET_DRAFT_STORE_ACTION,
    EDITED_DRAFT_STORE_ACTION,
    SAVE_DRAFT_SUCCESS_STORE_ACTION,
    REFRESH_DRAFT_STORE_ACTION,
    IS_EDITING_DRAFT_STORE_ACTION,
    SAVE_DRAFT_START_STORE_ACTION,
    RESET_DRAFT_STATE,
    SET_RE_ANALYZE_ACTION,
  } = draftSlice.actions;
  const { draft, isEditingDraft } = useSelector(
    (state: DraftInterfaceState) => state.draftStore,
  );
  const { user, loggedInUser } = useSelector(
    (state: UserState) => state.userStore,
  );
  const { drafts } = useDrafts(user?.id);
  const { UPDATE_USER, UPDATE_LOGGED_IN_USER } = userSlice.actions;
  const { handleClose, isEditing } = props;

  const [menuAnchors, setMenuAnchors] = useState<{
    [key: string]: HTMLElement | null;
  }>({});
  const [headingMenuAnchors, setHeadingMenuAnchors] = useState<{
    [key: string]: HTMLElement | null;
  }>({});
  const [updatedDraft, setUpdatedDraft] = useState<PostDraftInterface | null>(
    null,
  );
  const fileInputRef = useRef(null);
  const removeTypename = (obj: any): any => {
    const { __typename, ...rest } = obj;

    Object.keys(rest).forEach((key) => {
      if (typeof rest[key] === 'object' && rest[key] !== null) {
        if (Array.isArray(rest[key])) {
          rest[key] = rest[key].map(removeTypename);
        } else {
          rest[key] = removeTypename(rest[key]);
        }
      }
    });

    return rest;
  };

  const cleanBlocks = (blocks: TextBlock[]): TextBlock[] => {
    const sanitizedBlocks = blocks?.map((block) => {
      const cleanedBlock = removeTypename(block);

      // Special handling for List type
      if (
        cleanedBlock.type === 'List' &&
        cleanedBlock.data?.items &&
        Array.isArray(cleanedBlock.data.items)
      ) {
        // Ensure we deeply clean the items array
        cleanedBlock.data = {
          ...cleanedBlock.data,
          items: cleanedBlock.data.items.map(removeTypename),
        };
      }

      return cleanedBlock;
    });

    return sanitizedBlocks;
  };

  // const initialBlocks = cleanBlocks(post.textBlocks);
  const [updateDraftMutation, { data, loading, error }] = useMutation(
    UPDATE_DRAFT_MUTATION,
    {
      variables: {
        input: {
          ...UPDATE_DRAFT_VARIABLES,
        },
      },
      onCompleted: ({ updateDraft }) => {
        setUpdatedDraft(updateDraft);
        dispatch(SAVE_DRAFT_START_STORE_ACTION(true));
        // Redux updates will still happen in the useEffect
      },
      onError: (ex) => {
        console.error('useMutation error =>', ex);
      },
    },
  );

  useEffect(() => {
    if (updatedDraft) {
      dispatch(RESET_DRAFT_STATE(true));
      dispatch(SAVE_DRAFT_START_STORE_ACTION(false));
      dispatch(EDITED_DRAFT_STORE_ACTION(updatedDraft)); //SET_EDITED_POST
      dispatch(SAVE_DRAFT_SUCCESS_STORE_ACTION(true)); //SAVE_UPDATED_SUCCESS
      dispatch(GET_DRAFT_STORE_ACTION(updatedDraft)); //GET_POST
      dispatch(REFRESH_DRAFT_STORE_ACTION(true)); //REFRESH_POST
    }
  }, [updatedDraft, GET_DRAFT_STORE_ACTION, dispatch]);

  const handleMenuClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    blockId: string,
  ) => {
    setMenuAnchors({ ...menuAnchors, [blockId]: event.currentTarget });
  };

  const handleMenuClose = (blockId: string) => {
    setMenuAnchors({ ...menuAnchors, [blockId]: null });
  };

  const handleOpenHeadingMenu = (
    event: React.MouseEvent<HTMLButtonElement>,
    blockId: string,
  ) => {
    setHeadingMenuAnchors({
      ...headingMenuAnchors,
      [blockId]: event.currentTarget,
    });
  };

  const handleCloseHeadingMenu = (blockId: string) => {
    setHeadingMenuAnchors({ ...headingMenuAnchors, [blockId]: null });
  };

  const handleHeadingLevelChange = (
    index: number,
    level: number,
    setFieldValue: any,
  ) => {
    setFieldValue(`blocks.${index}.data.level`, level);
  };

  interface UpdatedPayloadI {
    id: string;
    username: string;
    textBlocks: TextBlock[];
    status: DocumentStatus;
  }

  const handleSaveEditedDraft = (updatedPayload: UpdatedPayloadI) => {
    const storePayload = {
      ...draft,
      textBlocks: updatedPayload.textBlocks,
    };
    const filteredDrafts = drafts.filter(
      (draft: PostDraftInterface) => draft.id !== updatedPayload.id,
    );
    const updatedDrafts = [...filteredDrafts, storePayload];
    const updatedUser = {
      ...user,
      drafts: updatedDrafts,
    };
    const updatedLoggedInUser = {
      ...loggedInUser,
      drafts: updatedDrafts,
    };
    dispatch(UPDATE_USER(updatedUser));
    dispatch(UPDATE_LOGGED_IN_USER(updatedLoggedInUser));
    updateDraftMutation({ variables: { input: updatedPayload } })
      .then((res) => {
        // dispatch(GET_DRAFT_STORE_ACTION(res.data.updateDraftPost))
        dispatch(IS_EDITING_DRAFT_STORE_ACTION(false));
        handleClose();
      })
      .catch((e) => {
        console.error(e);
      });
  };

  const handlePreSaveEditedDraft = (payload: EditPostPayload) => {
    const blocks = cleanBlocks(
      payload.textBlocks ?? payload.blocks,
    ) as TextBlock[];
    const updatedPayload = {
      id: draftId as string,
      username: username as string,
      textBlocks: blocks,
      status: draft?.status as DocumentStatus,
    };
    // Only flag the analysis as stale if the draft actually had one. The flag
    // is a global singleton in draftStore and is only reset by createAnalysis'
    // onCompleted, so dispatching it unconditionally leaves the Analyze button
    // labelled "Re-Analyze" forever on drafts that were never analyzed (TRIL-186).
    dispatch(SET_RE_ANALYZE_ACTION(!!draft?.hasStoryAnalysis));
    dispatch(RESET_DRAFT_STATE(true));
    handleSaveEditedDraft(updatedPayload);
  };

  const initialValues = {
    blocks: cleanBlocks(
      (draft?.textBlocks as TextBlock[]) ?? draft?.blocks,
    )?.map((block) => {
      return {
        ...block,
        data: {
          ...block.data,
          // Ensure all possible fields are initialized
          paragraph: block.data?.paragraph || '',
          header: block.data?.header || '',
          level: block.data?.level || 1,
          items: block.data?.items || [],
          code: block.data?.code || '',
          caption: block.data?.caption || '',
          style: block.data?.style || 'unordered',
        },
      };
    }),
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handlePreSaveEditedDraft as any}
    >
      {({
        handleSubmit,
        values,
        setFieldValue,
        isSubmitting,
        errors,
        touched,
      }) => {
        const finalValues = {
          ...values.blocks,
        };

        return (
          <DraftFormRender
            blockTypes={[
              'Paragraph',
              'Heading',
              'List',
              'Image',
              'Code',
              'Quote',
            ]}
            errors={errors as any}
            fileInputRef={fileInputRef}
            handleBlockTypeChange={handleBlockTypeChange as any}
            handleCloseHeadingMenu={handleCloseHeadingMenu}
            handleHeadingLevelChange={handleHeadingLevelChange}
            handleMenuClick={handleMenuClick as any}
            handleMenuClose={handleMenuClose}
            handleOpenHeadingMenu={handleOpenHeadingMenu}
            handleSave={handlePreSaveEditedDraft}
            handleSubmit={handleSubmit}
            headingMenuAnchors={headingMenuAnchors}
            isSubmitting={isSubmitting}
            menuAnchors={menuAnchors}
            payload={{
              username: username as string,
              textBlocks: values.blocks,
              draftId: draftId as string,
              status: draft?.status,
            }}
            setFieldValue={setFieldValue}
            touched={touched}
            values={values as any}
            isEditing={isEditing as boolean}
          />
        );
      }}
    </Formik>
  );
};
